import { createContext, useContext, useEffect, useState } from "react";
import getUserData from "api/getUserData";
import { medical } from "@constants/medical";
import { useTranslation } from "react-i18next";

// --- 1. Define Separate Contexts ---
export const AuthStatusContext = createContext(); // For token, login/logout
export const UserDataContext = createContext(); // For user object, subscription, flags
export const AuthLoadingContext = createContext(); // For initial loading state

const WORDPRESS_DOMAIN = process.env.REACT_APP_WORDPRESS_DOMAIN;
const LARAVEL_API = process.env.REACT_APP_LARAVEL_API;

// --- Custom Hooks for Consumers ---
export const useAuthStatus = () => useContext(AuthStatusContext);
export const useUserData = () => useContext(UserDataContext);
export const useAuthLoading = () => useContext(AuthLoadingContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("access_token"));
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [medicalView, setMedicalView] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [weeklyCheckIn, setWeeklyCheckIn] = useState(false);
  const [isActionLocked, setIsActionLocked] = useState(false);
  const [showCreatePlan, setShowCreatePlan] = useState(false);

  const [language, setLanguage] = useState("en");

  const { i18n } = useTranslation();

  const handleSetLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setLanguage(lang);
  };

  const login = () => {
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem("pre_login_path", currentPath);
    const returnUrl = encodeURIComponent(window.location.origin);
    window.location.href = `${WORDPRESS_DOMAIN}/login?redirect_to=${returnUrl}`;
  };

  const syncLogoutWithWordPress = async (currentToken) => {
    try {
      await fetch(`${WORDPRESS_DOMAIN}/wp-json/custom-sso/v1/react-logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: currentToken,
          source: "react_dashboard",
          timestamp: new Date().toISOString(),
        }),
        credentials: "include",
      });
    } catch (error) {
      console.error("WordPress logout notification failed:", error);
    }
  };

  const performSSOLogout = async () => {
    const currentToken = localStorage.getItem("access_token");
    try {
      if (currentToken) {
        await fetch(`${LARAVEL_API}/api/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "Content-Type": "application/json",
          },
        });
      }
      await syncLogoutWithWordPress(currentToken);
    } catch (error) {
      console.error("SSO logout sync failed:", error);
    } finally {
      localStorage.removeItem("access_token");
      setUser(null);
      setToken(null);
      window.location.href = "/login";
    }
  };

  const logout = async () => {
    await performSSOLogout();
  };

  const isAuthenticated = !!(token && user);

  const fetchUser = async () => {
    try {
      const userData = await getUserData();

      const subscriptionData = userData?.subscriptions;
      const isMedicalCompleted =
        userData?.is_form_completed === medical.ALLOWED;
      const actualLanguage =
        userData?.language === "English"
          ? "en"
          : userData?.language === "Nederlands"
          ? "nl"
          : "en";

      setUser(userData);
      localStorage?.setItem(
        "is_google_fit_connect",
        userData?.is_google_fit_connect
      );
      setSubscription(subscriptionData);
      setWeeklyCheckIn(
        userData?.is_manual_edit_allowed &&
          subscriptionData?.status === "active"
      );
      setShowCreatePlan(userData?.create_my_plan);
      setMedicalView(
        isMedicalCompleted && subscriptionData?.status === "active"
      );
      handleSetLanguage(actualLanguage);
    } catch (error) {
      console.error("Failed to fetch user:", error);
    } finally {
      setIsAuthLoading(false);
    }
  };

  // Only fetch once on mount
  useEffect(() => {
    if (localStorage.getItem("access_token")) {
      fetchUser();
    } else {
      setIsAuthLoading(false);
    }
  }, []);

  const authStatusValue = {
    token,
    login,
    logout,
    isAuthenticated,
    setToken,
  };

  const userDataValue = {
    user,
    setUser,
    subscription,
    setSubscription,
    medicalView,
    setMedicalView,
    language,
    handleSetLanguage,
    weeklyCheckIn,
    setWeeklyCheckIn,
    isActionLocked,
    setIsActionLocked,
    showCreatePlan,
    setShowCreatePlan,
  };

  const authLoadingValue = {
    isAuthLoading,
    setIsAuthLoading,
  };

  return (
    <AuthStatusContext.Provider value={authStatusValue}>
      <UserDataContext.Provider value={userDataValue}>
        <AuthLoadingContext.Provider value={authLoadingValue}>
          {children}
        </AuthLoadingContext.Provider>
      </UserDataContext.Provider>
    </AuthStatusContext.Provider>
  );
};
